import LocallyDropdown from '@components/Product/_LocallyDropdown';
import TierTwoVariations from '@components/Product/Details/_TierTwoVariations';
import Markdown from '@repo/ui/src/components/Markdown';
import React, { useContext } from 'react';
import ProductHeading from '@components/Product/Details/_ProductHeading';
import { LinkProps } from '@repo/ui/src/types/link';
import Product from '@models/product';
import { ProductContext } from '@context/ProductProvider';
import CallToActionButton from '@components/Product/CallToActionButton';
import PromotionsAndPricing from '@components/Product/Details/_PromotionsAndPricing';
import CompatibleProducts from '@components/Product/Details/_CompatibleProducts';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import AdditionalInformation from '@components/Product/Details/_AdditionalInformation';

interface Props {
  essentials?: Array<{
    id: string;
  }>;
  product: Product;
  promoMarkdown?: string;
  ownerManual?: {
    ownerManualLink: Array<LinkProps>;
  };
  prop65?: {
    title: string;
    warningBody: string;
  };
  compatibleWith?: Array<{
    image: any;
    link?: LinkProps;
  }>;
}

const TierTwoProductSelection = ({ product, compatibleWith, prop65 }: Props) => {
  const { t } = useTranslation();
  const { totalPrice: _totalPrice, addToCart, isLoadingAddToCart } = useContext(ProductContext);

  const [quantity, setQuantity] = React.useState(1);
  const totalPrice = React.useMemo(() => {
    return _totalPrice && _totalPrice !== '0'
      ? Number(_totalPrice)
      : [product].reduce((val, item) => val + item.price, 0);
  }, [_totalPrice, product]);

  return (
    <div className="tw-grid tw-gap-y-8">
      <div className="tw-gap-y-4 md:tw-grid tw-hidden">
        <ProductHeading product={product} className="" hideDescription />
        <PromotionsAndPricing
          promotions={product?.promotions}
          product={product}
          className="tw-flex"
          isNewTemplate
        />
        {product.shortDescription && (
          <div className="tw-pt-4">
            <Markdown bodySize="xLarge">{product.shortDescription}</Markdown>
          </div>
        )}
      </div>
      <TierTwoVariations product={product} />
      <div className="tw-grid tw-gap-y-6">
        <div className="tw-flex tw-items-stretch">
          <div className="tw-flex tw-flex-grow tw-mr-4">
            <div className="tw-aspect-square tw-w-15 tw-border-surface-450 tw-border-2 tw-bg-white tw-flex tw-align-center tw-p-1 tw-rounded-md">
              <label className="tra-text-web-body-m tw-text-text-500 tw-text-center">
                {t('productDetailPageContent.quantityInputLabel', undefined, {
                  default: 'Qty',
                })}
                <input
                  min={1}
                  max={999}
                  type="number"
                  value={quantity}
                  className="tw-text-surface-700 tw-justify-center tw-w-full tw-pt-2 tw-text-center"
                  onChange={(e) => {
                    const newQuantity = Number(e.target.value);
                    setQuantity(isNaN(newQuantity) ? 0 : newQuantity);
                  }}
                  data-testid="quantityInput"
                />
              </label>
            </div>
          </div>
          <CallToActionButton
            product={product}
            buttonProcessing={isLoadingAddToCart}
            processingText=""
            customProductPrice={totalPrice}
            buttonProps={{
              disabled: isLoadingAddToCart,
              full: true,
              tabIndex: 0,
              className: isLoadingAddToCart
                ? 'disabled:tw-bg-action-400'
                : 'disabled:tw-bg-surface-400',
            }}
            onClickAddToCart={() => {
              addToCart(quantity);
            }}
          />
        </div>

        {product?.shop?.upc && !product.shop.disableLocally && (
          <LocallyDropdown upc={product.shop.upc} />
        )}
        {compatibleWith && <CompatibleProducts compatibleWith={compatibleWith} />}
        {prop65 && <AdditionalInformation prop65={prop65} />}
      </div>
    </div>
  );
};

export default TierTwoProductSelection;
